import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AUTH_TOKEN_KEY,
  buildUpdateTeamBodyFromAdminInfo,
  canEditTeamInfo,
  fetchAdminTeamInfo,
  fetchSystemUserInfo,
  postUserUpdateTeam,
  type AdminTeamInfo,
} from '@/api';
import { GooglePlacesAutocomplete } from '@/components/ui/GooglePlacesAutocomplete';
import { CountrySelect } from '@/components/ui/CountrySelect';
import { FormSummaryMessage, InlineFieldError } from '@/components/ui/FormFeedback';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import {
  DEFAULT_TEAM_COUNTRY,
  normalizeTeamCountry,
  teamCountryToPlacesCode,
} from '@/constants/teamCountries';
import { validateTeamPhone } from '@/lib/teamPhone';

type FormData = {
  name: string;
  logo_path: string;
  team_profile: string;
  country: string;
  fullAddress: string;
  phone: string;
  email: string;
  whatsapp: string;
};

type TeamFieldErrors = Partial<Pick<FormData, 'name' | 'phone' | 'email' | 'whatsapp'>>;

const inputBaseClass =
  'w-full h-8 px-2.5 text-xs font-normal rounded-md border outline-none transition disabled:bg-gray-50 disabled:text-gray-400 placeholder:font-normal placeholder:text-xs placeholder:text-gray-400';

function inputClass(invalid: boolean): string {
  return `${inputBaseClass} ${
    invalid
      ? 'border-red-300 bg-red-50/35 focus:border-red-400'
      : 'border-gray-200 bg-white focus:border-green-500'
  }`;
}

function validateWhatsappNumber(value: string): string | undefined {
  const wa = value.trim();
  if (!wa) return undefined;
  const digits = wa.replace(/\D/g, '');
  if (!/^\+?[\d\s-]{8,20}$/.test(wa) || digits.length < 8 || digits.length > 15) {
    return 'Enter a valid WhatsApp number with country code.';
  }
  return undefined;
}

function validateTeamFields(
  f: Pick<FormData, 'name' | 'phone' | 'email' | 'whatsapp' | 'country'>,
): TeamFieldErrors {
  const e: TeamFieldErrors = {};
  if (!f.name.trim()) e.name = 'Team name is required.';
  const phoneError = validateTeamPhone(f.phone, f.country, true);
  if (phoneError) e.phone = phoneError;
  const em = f.email.trim();
  if (!em) e.email = 'Email is required.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) e.email = 'Please enter a valid email address.';
  const waErr = validateWhatsappNumber(f.whatsapp);
  if (waErr) e.whatsapp = waErr;
  return e;
}

const emptyForm: FormData = {
  name: '',
  logo_path: '',
  team_profile: '',
  country: DEFAULT_TEAM_COUNTRY,
  fullAddress: '',
  phone: '',
  email: '',
  whatsapp: '',
};

function adminTeamToForm(team: AdminTeamInfo): FormData {
  return {
    name: team.name,
    logo_path: team.logo_path ?? '',
    team_profile: team.team_profile ?? '',
    country: normalizeTeamCountry(team.country),
    fullAddress: team.address ?? '',
    phone: team.phone ?? '',
    email: team.email ?? '',
    whatsapp: team.whatsapp_number ?? '',
  };
}

const SUBMIT_MESSAGE_DURATION_MS = 1500;

export default function TeamSettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ text: string; success: boolean } | null>(null);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<TeamFieldErrors>({});
  const [form, setForm] = useState<FormData>(emptyForm);
  const [teamSnapshot, setTeamSnapshot] = useState<AdminTeamInfo | null>(null);
  const submitMessageTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (submitMessageTimerRef.current) {
        window.clearTimeout(submitMessageTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(AUTH_TOKEN_KEY)) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    fetchSystemUserInfo()
      .then((profile) => {
        if (cancelled) return;
        if (!canEditTeamInfo(profile)) {
          navigate('/team', { replace: true });
          return;
        }
        return fetchAdminTeamInfo();
      })
      .then((team) => {
        if (cancelled || !team) return;
        setTeamSnapshot(team);
        setForm(adminTeamToForm(team));
      })
      .catch(() => {
        if (!cancelled) navigate('/team', { replace: true });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const showSubmitMessage = (text: string, success: boolean) => {
    if (submitMessageTimerRef.current) {
      window.clearTimeout(submitMessageTimerRef.current);
    }
    setSubmitMessage({ text, success });
    if (success) setSaved(true);
    submitMessageTimerRef.current = window.setTimeout(() => {
      submitMessageTimerRef.current = null;
      setSubmitMessage(null);
      setSaving(false);
      if (success) setSaved(false);
    }, SUBMIT_MESSAGE_DURATION_MS);
  };

  useEffect(() => {
    const state = location.state as { logoPath?: string; logoSaved?: boolean } | null;
    const logoPath = state?.logoPath?.trim();
    if (!logoPath) return;
    setForm((prev) => ({ ...prev, logo_path: logoPath }));
    if (state?.logoSaved) {
      showSubmitMessage('Team logo updated successfully.', true);
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (location.hash !== '#invoice') return;
    navigate('/team/invoice', { replace: true });
  }, [location.hash, navigate]);

  const handleChange = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'name' || field === 'phone' || field === 'email' || field === 'whatsapp') {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSave = async (e?: FormEvent) => {
    e?.preventDefault();
    setFormError('');
    const verr = validateTeamFields(form);
    setFieldErrors(verr);
    if (Object.keys(verr).length > 0) {
      requestAnimationFrame(() => {
        if (verr.name) document.getElementById('team-settings-name')?.focus();
        else if (verr.phone) document.getElementById('team-settings-phone')?.focus();
        else if (verr.email) document.getElementById('team-settings-email')?.focus();
        else if (verr.whatsapp) document.getElementById('team-settings-whatsapp')?.focus();
      });
      return;
    }
    if (!teamSnapshot) {
      setFormError('Team information is not loaded yet.');
      return;
    }

    setSaving(true);
    const result = await postUserUpdateTeam(
      buildUpdateTeamBodyFromAdminInfo(teamSnapshot, {
        name: form.name,
        logo_path: form.logo_path || undefined,
        phone: form.phone,
        email: form.email,
        address: form.fullAddress || undefined,
        country: form.country,
        team_profile: form.team_profile || undefined,
        whatsapp_number: form.whatsapp.trim(),
      }),
    );

    if (result.error) {
      setFormError(result.message);
      showSubmitMessage(result.message, false);
      return;
    }

    setFieldErrors({});
    setFormError('');
    try {
      const team = await fetchAdminTeamInfo();
      setTeamSnapshot(team);
      setForm(adminTeamToForm(team));
    } catch {
      // keep local form on refresh failure
    }

    showSubmitMessage(result.message || 'Changes saved successfully', true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-green-500 text-2xl" />
          <p className="text-gray-400 text-xs mt-2">Loading team settings…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <SubpageHeader
        title="Team Settings"
        subtitle="Edit your team profile"
        onBack={() => navigate('/me')}
      />

      <form
        lang="en"
        noValidate
        onSubmit={(e) => void handleSave(e)}
        className="px-4 pt-3 space-y-2.5 pb-[calc(5.25rem+env(safe-area-inset-bottom))]"
      >
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <button
            type="button"
            onClick={() => navigate('/team/settings/logo')}
            disabled={saving}
            className="w-full flex items-center gap-3 cursor-pointer text-left disabled:opacity-60 disabled:cursor-not-allowed group"
          >
            <div className="relative w-14 h-14 rounded-lg bg-green-50 border-2 border-dashed border-green-200 flex items-center justify-center overflow-hidden flex-shrink-0 group-hover:border-green-400 group-hover:bg-green-100/80 transition-colors">
              {form.logo_path?.trim() ? (
                <img src={form.logo_path} alt="Team logo" className="w-full h-full object-cover" />
              ) : (
                <i className="ri-image-add-line text-green-500 text-xl" />
              )}
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <i className="ri-camera-line text-white text-sm" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-gray-900 font-semibold text-sm">Team Logo</div>
              <div className="text-gray-400 text-xs mt-0.5">
                Used on invoices · square (1:1) required
              </div>
            </div>
            <i className="ri-arrow-right-s-line text-gray-300 text-xl flex-shrink-0" />
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2.5">
          <div>
            <label htmlFor="team-settings-name" className="block text-xs font-normal text-gray-700 mb-1">
              Team Name <span className="text-red-400">*</span>
            </label>
            <input
              id="team-settings-name"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g. Sydney Field Services"
              disabled={saving}
              aria-invalid={!!fieldErrors.name}
              aria-describedby={fieldErrors.name ? 'team-name-error' : undefined}
              className={inputClass(!!fieldErrors.name)}
            />
            <InlineFieldError id="team-name-error" message={fieldErrors.name} />
          </div>
          <div>
            <label htmlFor="team-settings-phone" className="block text-xs font-normal text-gray-700 mb-1">
              Phone <span className="text-red-400">*</span>
            </label>
            <input
              id="team-settings-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="+61 400 000 000"
              disabled={saving}
              aria-invalid={!!fieldErrors.phone}
              aria-describedby={fieldErrors.phone ? 'team-phone-error' : undefined}
              className={inputClass(!!fieldErrors.phone)}
            />
            <InlineFieldError id="team-phone-error" message={fieldErrors.phone} />
          </div>
          <div>
            <label htmlFor="team-settings-whatsapp" className="block text-xs font-normal text-gray-700 mb-1">
              WhatsApp
            </label>
            <input
              id="team-settings-whatsapp"
              type="tel"
              inputMode="tel"
              value={form.whatsapp}
              onChange={(e) => handleChange('whatsapp', e.target.value)}
              placeholder="+61 400 000 000"
              disabled={saving}
              aria-invalid={!!fieldErrors.whatsapp}
              aria-describedby={fieldErrors.whatsapp ? 'team-whatsapp-error' : undefined}
              className={inputClass(!!fieldErrors.whatsapp)}
            />
            {fieldErrors.whatsapp ? (
              <InlineFieldError id="team-whatsapp-error" message={fieldErrors.whatsapp} />
            ) : (
              <p className="text-gray-400 text-[11px] mt-1">Include country code. Optional.</p>
            )}
          </div>
          <div>
            <label htmlFor="team-settings-email" className="block text-xs font-normal text-gray-700 mb-1">
              Email <span className="text-red-400">*</span>
            </label>
            <input
              id="team-settings-email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="contact@yourteam.com"
              disabled={saving}
              aria-invalid={!!fieldErrors.email}
              aria-describedby={fieldErrors.email ? 'team-email-error' : undefined}
              className={inputClass(!!fieldErrors.email)}
            />
            <InlineFieldError id="team-email-error" message={fieldErrors.email} />
          </div>
          <div>
            <label htmlFor="team-settings-country" className="block text-xs font-normal text-gray-700 mb-1">
              Country / Region
            </label>
            <CountrySelect
              id="team-settings-country"
              value={form.country}
              onChange={(code) => handleChange('country', code)}
              disabled={saving}
            />
          </div>
          <div>
            <label htmlFor="team-settings-address" className="block text-xs font-normal text-gray-700 mb-1">
              Address
            </label>
            <GooglePlacesAutocomplete
              id="team-settings-address"
              key={form.country}
              value={form.fullAddress}
              onChange={(address) => handleChange('fullAddress', address)}
              disabled={saving}
              country={teamCountryToPlacesCode(form.country)}
              placeholder="Start typing to search addresses…"
              className={inputClass(false)}
            />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2.5">
          <div className="text-xs font-semibold text-gray-800">Team Profile</div>
          <div>
            <label htmlFor="team-settings-profile" className="sr-only">
              Team Profile
            </label>
            <textarea
              id="team-settings-profile"
              value={form.team_profile}
              onChange={(e) => handleChange('team_profile', e.target.value)}
              placeholder="Describe your team, services, and expertise…"
              rows={4}
              disabled={saving}
              className={`${inputBaseClass} h-auto py-2 border-gray-200 bg-white focus:border-green-500 resize-none leading-relaxed`}
            />
          </div>
        </div>

        <FormSummaryMessage message={formError} />

        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="mx-auto w-full" style={{ maxWidth: 480 }}>
            <button
              type="submit"
              disabled={saving}
              className="w-full h-11 rounded-lg bg-green-500 hover:bg-green-600 text-white font-normal text-sm cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-base" />
                  Saving…
                </>
              ) : saved ? (
                <>
                  <i className="ri-check-line text-base" />
                  Saved
                </>
              ) : (
                <>
                  <i className="ri-save-line text-base" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {submitMessage && (
        <div className="fixed top-16 left-0 right-0 z-50 flex justify-center px-4 pointer-events-none">
          <div className="bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-md shadow-lg flex items-center gap-2 max-w-[90%]">
            <i
              className={
                submitMessage.success
                  ? 'ri-checkbox-circle-fill text-green-400'
                  : 'ri-error-warning-line text-amber-400'
              }
            />
            <span className="leading-snug">{submitMessage.text}</span>
          </div>
        </div>
      )}
    </div>
  );
}
